import React from "react"
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {formatISO} from "date-fns";

import Page from "../Page";
import useDataTableFilters from "../../components/DataTable/useDataTableFilters";

import MDBox from "materialDashboard/components/MDBox";
import MDButton from "materialDashboard/components/MDButton";
import Card from "@mui/material/Card";
import TableDataLoader from "./IncidentTable/TableDataLoader";
import {getSeasonFromDate} from "../../utils/date";
import {useIncidentRegistryQuery, useIncidentSettingsQuery} from "./query";
import {useAuth} from "../../auth/AuthProvider";
import {useCurrentUser} from "../../auth/CurrentUserProvider";
import IncidentMap from "./IncidentMap";
import useIncidentMap from "./useIncidentMap";
import Box from "@mui/material/Box";
import IncidentFeatureChip from "../../components/maps/incidentFeature/IncidentFeatureChip";
import LoadingOverlay from "../../components/LoadingOverlay";
import {ExpandButtonIcon, useExpandButtonIcon} from "../../components/maps/ExpandButtonIcon";


const Incidents = () => {
    const {t} = useTranslation(['incidents']);
    const navigate = useNavigate();
    const {workgroupAuthorization} = useAuth();
    const {currentUser} = useCurrentUser();

    const incidentRegistry = useIncidentRegistryQuery();
    const incidentSettings = useIncidentSettingsQuery();

    // data states
    const currentSeason = getSeasonFromDate(new Date());
    const seasonStartISO = formatISO(currentSeason.from);
    const seasonEndISO = formatISO(currentSeason.to);

    const selectedWorkgroups = currentUser.data.role === "ADMIN" ? [] : currentUser.data.workgroups;

    const filterProps = {
        initialValues: {
            selectedWorkgroups,
            dateGte: seasonStartISO,
            dateLte: seasonEndISO,
        }
    };
    const initialFiltersValue = {
        workgroup_id__in: selectedWorkgroups.map(w => w.id),
        date__gte: seasonStartISO,
        date__lte: seasonEndISO,
    }

    const filterState = useDataTableFilters({initialFiltersValue});

    // Map state
    const map = useIncidentMap({incidentsFilters: filterState.filters}); // ref is need to call flyTo and other methods on the map
    const expandButtonState = useExpandButtonIcon();

    if (incidentRegistry.isLoading || incidentSettings.isLoading) {
        return <LoadingOverlay />
    }

    return <Page>
        <MDBox px={2}>
            <Box justifyContent="space-between" alignItems="center" display="flex" flexWrap="wrap" gap={2} mb={2}>
                {
                    workgroupAuthorization.incidents.create &&
                    <MDButton variant="gradient" color="info" onClick={() => navigate("/incidents/new")}>
                        {t('newIncident')}
                    </MDButton>
                }
                <ExpandButtonIcon {...expandButtonState} />
            </Box>
            <MDBox ref={map.mapBoxRef}
                   borderRadius="xl"
                   bgColor="grey-300"
                   sx={{
                       height: expandButtonState.mapHeight,
                       scrollMarginTop: 100,
                       overflow: "hidden",
                       mt: 2,
                       boxShadow: 1,
                       position: "relative",
                       zIndex: 2
                   }}>
                <IncidentMap mapRef={map.mapRef}
                             incidentsGeoJSON={map.geoJSON}
                             incidentRegistry={incidentRegistry}
                             incidentSettings={incidentSettings}
                             getSelectedFeatures={map.getSelectedFeatures}
                             onFeatureClick={map.handleFeatureClick} />
            </MDBox>
            <Card sx={{pt: 3, mt: -3, zIndex: 1}}>
                {
                    map.selectedFeatures.length > 0 && <Box sx={{display: "flex", flexWrap: "wrap", gap: 1, pt: 3, px: 3}}>
                        {
                            map.selectedFeatures.map(f =>
                                <IncidentFeatureChip key={f.properties.id}
                                                     feature={f}
                                                     onDelete={() => map.toggleFeature(f)}/>
                            )
                        }
                    </Box>
                }
                <TableDataLoader filterState={filterState}
                                 filterProps={filterProps}
                                 incidentRegistry={incidentRegistry}
                                 incidentSettings={incidentSettings}
                                 dataTableId="incidents"
                                 map={map}/>
            </Card>
        </MDBox>

    </Page>
}


export default Incidents;